import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, CheckCircle, XCircle, Shield, Trash2, Unlock, Clock, AlertTriangle } from 'lucide-react';
import { CategoryTreeView } from '@/components/admin/CategoryTree';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { AdminInvitesCard } from '@/components/admin/AdminInvitesCard';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export const CategoriesTab = ({ categories, onAdd, onEdit, onDelete, onAddChild, t }) => (
  <Card>
    <CardHeader className="flex flex-row items-center justify-between">
      <div><CardTitle>{t('admin.categoryTree')}</CardTitle><CardDescription>{t('admin.categoryTreeDesc')}</CardDescription></div>
      <Button onClick={onAdd} data-testid="add-category-btn"><Plus className="h-4 w-4 mr-2" />{t('admin.addCategory')}</Button>
    </CardHeader>
    <CardContent>
      {categories.length > 0 ? (
        <CategoryTreeView categories={categories} onEdit={onEdit} onDelete={onDelete} onAddChild={onAddChild} />
      ) : (
        <p className="text-muted-foreground text-center py-8">{t('admin.noCategoriesFound')}</p>
      )}
    </CardContent>
  </Card>
);

const ROLE_OPTIONS = [
  { value: 'user', label: 'Benutzer', color: 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300' },
  { value: 'site_manager', label: 'Site Admin', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' },
  { value: 'group_admin', label: 'Gruppen Admin', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400' },
  { value: 'admin', label: 'Platform Admin', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' },
];

const getRoleBadgeClass = (role) => {
  const r = ROLE_OPTIONS.find(o => o.value === role);
  return r ? r.color : 'bg-slate-100 text-slate-800';
};

const getRoleLabel = (role) => {
  const r = ROLE_OPTIONS.find(o => o.value === role);
  return r ? r.label : role;
};

export const UsersTab = ({ users, sites, onApprove, onRoleChange, onDelete, onReload, t, isAdmin, isGroupAdmin }) => {
  const [editingUser, setEditingUser] = useState(null);
  const [newRole, setNewRole] = useState('');
  const [newSiteId, setNewSiteId] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [unlockingId, setUnlockingId] = useState(null);
  const { user: me } = useAuth();
  const adminCount = users.filter(u => u.role === 'admin').length;
  const canDelete = (u) => isAdmin && u.user_id !== me?.user_id && (u.role !== 'admin' || adminCount > 1);

  const canEditRole = isAdmin || isGroupAdmin;
  const allowedRoles = isAdmin
    ? ROLE_OPTIONS
    : ROLE_OPTIONS.filter(r => r.value !== 'admin');

  const authHeader = () => {
    const tk = localStorage.getItem('token');
    return tk ? { Authorization: `Bearer ${tk}` } : {};
  };

  const handleUnlock = async (u) => {
    setUnlockingId(u.user_id);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const res = await axios.post(`${API}/api/admin/users/${u.user_id}/unlock`, {}, { headers: authHeader() });
      toast.success(res.data?.unlocked_ip
        ? `Entsperrt — IP ${res.data.unlocked_ip} freigegeben`
        : 'Fehlversuch-Zähler zurückgesetzt');
      if (onReload) await onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Entsperren fehlgeschlagen');
    } finally {
      setUnlockingId(null);
    }
  };

  const formatDateTime = (iso) => {
    if (!iso) return null;
    try { return new Date(iso).toLocaleString('de-DE'); } catch { return iso; }
  };

  const startEdit = (u) => {
    setEditingUser(u.user_id);
    setNewRole(u.role);
    setNewSiteId(u.site_id || '');
  };

  const cancelEdit = () => {
    setEditingUser(null);
    setNewRole('');
    setNewSiteId('');
  };

  const saveRole = async (userId) => {
    await onRoleChange(userId, newRole, newSiteId && newSiteId !== 'none' ? newSiteId : null);
    cancelEdit();
  };

  return (
    <div className="space-y-6">
    {isAdmin && <AdminInvitesCard />}
    <Card>
      <CardHeader>
        <CardTitle>{t('admin.users')}</CardTitle>
        <CardDescription>{isGroupAdmin ? (t('admin.groupUsersDesc') || t('admin.usersDesc')) : t('admin.usersDesc')}</CardDescription>
      </CardHeader>
      <CardContent>
        {users.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">{t('admin.noUsers')}</p>
        ) : (
          <div className="space-y-3" data-testid="users-list">
            {users.map((u) => (
              <div key={u.user_id} className="flex items-center justify-between p-4 border rounded-lg" data-testid={`user-row-${u.user_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="relative shrink-0">
                      <div className={`w-2 h-2 rounded-full ${u.approved ? 'bg-green-500' : 'bg-yellow-500'}`} />
                      {u.online && (
                        <span
                          className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-500 ring-2 ring-background animate-pulse"
                          data-testid={`user-online-dot-${u.user_id}`}
                          title="Online (letzte 5 Min. aktiv)"
                        />
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold truncate flex items-center gap-2">
                        <span>{u.name}</span>
                        {u.online && (
                          <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400 text-[10px] px-1.5 py-0" data-testid={`user-online-badge-${u.user_id}`}>
                            Online
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground truncate">{u.email}</p>
                    </div>
                    {editingUser === u.user_id ? (
                      <div className="flex items-center gap-2 flex-wrap">
                        <Select value={newRole} onValueChange={setNewRole}>
                          <SelectTrigger className="w-[160px] h-8 text-xs" data-testid={`role-select-${u.user_id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {allowedRoles.map(r => (
                              <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {sites && (newRole === 'site_manager' || newRole === 'user') && (
                          <Select value={newSiteId} onValueChange={setNewSiteId}>
                            <SelectTrigger className="w-[160px] h-8 text-xs" data-testid={`site-select-${u.user_id}`}>
                              <SelectValue placeholder={t('admin.bug.selectSite') || 'Site zuweisen...'} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">{t('admin.simple.noSite')}</SelectItem>
                              {sites.map(s => (
                                <SelectItem key={s.site_id} value={s.site_id}>{s.site_name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        <Button size="sm" className="h-7 text-xs bg-green-500 hover:bg-green-600" onClick={() => saveRole(u.user_id)} data-testid={`save-role-${u.user_id}`}>
                          {t('common.save')}
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={cancelEdit}>
                          {t('common.cancel')}
                        </Button>
                      </div>
                    ) : (
                      <>
                        <Badge className={`${getRoleBadgeClass(u.role)} text-xs`} data-testid={`user-role-badge-${u.user_id}`}>
                          {getRoleLabel(u.role)}
                        </Badge>
                        <Badge className={u.approved ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'} data-testid={`user-status-${u.user_id}`}>
                          {u.approved ? t('admin.approved') : t('admin.pending')}
                        </Badge>
                        {(u.failed_login_count ?? 0) > 0 && (
                          <Badge
                            className={`text-xs gap-1 ${(u.failed_login_count >= 5)
                              ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                              : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400'}`}
                            data-testid={`user-failed-logins-${u.user_id}`}
                          >
                            <AlertTriangle className="h-3 w-3" />
                            {u.failed_login_count} Fehlversuch{u.failed_login_count === 1 ? '' : 'e'}
                          </Badge>
                        )}
                        {u.ip_locked_until && (
                          <Badge className="bg-red-600 text-white text-xs gap-1" data-testid={`user-ip-locked-${u.user_id}`} title={`Bis ${formatDateTime(u.ip_locked_until)}`}>
                            <Shield className="h-3 w-3" />
                            IP gesperrt
                          </Badge>
                        )}
                        {u.site_id && sites && (() => {
                          const site = sites.find(s => s.site_id === u.site_id);
                          return site ? <span className="text-xs text-muted-foreground">{site.site_name}</span> : null;
                        })()}
                      </>
                    )}
                  </div>
                  <div className="mt-1 ml-5 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
                    <span>{t('admin.registeredAt')}: {new Date(u.created_at).toLocaleDateString()}</span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {u.last_login_at
                        ? <>Zuletzt eingeloggt: <span className="font-medium" data-testid={`user-last-login-${u.user_id}`}>{formatDateTime(u.last_login_at)}</span>{u.last_login_ip ? <span className="font-mono ml-1">({u.last_login_ip})</span> : null}</>
                        : <span data-testid={`user-last-login-${u.user_id}`}>Noch nie eingeloggt</span>}
                    </span>
                  </div>
                </div>
                <div className="flex gap-1 shrink-0 ml-2">
                  {canEditRole && u.role !== 'admin' && editingUser !== u.user_id && (
                    <Button size="sm" variant="ghost" className="gap-1 h-8" onClick={() => startEdit(u)} data-testid={`edit-role-btn-${u.user_id}`}>
                      <Shield className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  {canEditRole && ((u.failed_login_count ?? 0) > 0 || u.ip_locked_until) && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1 h-8 text-xs border-red-300 text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30"
                      onClick={() => handleUnlock(u)}
                      disabled={unlockingId === u.user_id}
                      data-testid={`unlock-user-btn-${u.user_id}`}
                    >
                      <Unlock className="h-3.5 w-3.5" />
                      {unlockingId === u.user_id ? '…' : 'Entsperren'}
                    </Button>
                  )}
                  {canDelete(u) && (
                    <Button size="sm" variant="ghost" className="gap-1 h-8 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30" onClick={() => setDeleteTarget(u)} data-testid={`delete-user-btn-${u.user_id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  {!u.approved ? (
                    <Button size="sm" className="bg-green-500 hover:bg-green-600 gap-1 h-8" onClick={() => onApprove(u.user_id, true)} data-testid={`approve-user-btn-${u.user_id}`}>
                      <CheckCircle className="h-4 w-4" />{t('admin.approveUser')}
                    </Button>
                  ) : u.role !== 'admin' ? (
                    <Button size="sm" variant="outline" className="gap-1 h-8 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => onApprove(u.user_id, false)} data-testid={`revoke-user-btn-${u.user_id}`}>
                      <XCircle className="h-4 w-4" />{t('admin.revokeUser')}
                    </Button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="delete-user-dialog-title">{t('admin.deleteUserTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('admin.deleteUserConfirm', { name: deleteTarget?.name, email: deleteTarget?.email })}
              {deleteTarget?.role === 'admin' && (
                <span className="block mt-2 font-semibold text-red-600" data-testid="delete-admin-hint">{t('adminInvite.deleteAdminHint')}</span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-user-cancel-btn">{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="delete-user-confirm-btn"
              onClick={() => { onDelete(deleteTarget.user_id); setDeleteTarget(null); }}
            >
              {t('admin.deleteUser')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
    </div>
  );
};
